/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * BoardLoader — the chrono. What the board shows while a tab is being fetched.
 *
 * A stopwatch: the hand sweeps a full turn while a ring fills the dial behind
 * it. Chosen from six loaders on the design canvas (2026-11-02) — the most
 * literal to a race and the calmest, so it never competes with the page it is
 * standing in for.
 *
 * It replaced the words "Loading results…", which read as the page having
 * stopped rather than working.
 *
 * THE GHOST KEEPS THE SHAPE. Underneath the dial sits a card at the real
 * table's radius, with its tab strip and rows at the real 60px height, so when
 * the data lands the page fills in rather than jumping.
 *
 * RESPECTS prefers-reduced-motion. A leaderboard is exactly the kind of page
 * somebody opens on a phone while catching their breath. With motion reduced
 * the dial holds at three-quarters and nothing moves.
 */

import React from 'react';

export const INK = '#241708';
export const MUTED = '#6B5844';
export const ACCENT = '#C2410C';
export const HAIR = 'rgba(36, 23, 8, .10)';

/** The dial's circumference, for the ring that fills it: 2πr at r = 52. */
const RING = 327;

const CSS = `
@keyframes mb-sweep { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
@keyframes mb-fill  { from { stroke-dashoffset: ${RING} } to { stroke-dashoffset: 0 } }
@keyframes mb-shim  { from { background-position: -260px 0 } to { background-position: 520px 0 } }

.mb-hand { transform-origin: 60px 60px; animation: mb-sweep 1.6s cubic-bezier(.4,0,.2,1) infinite; }
.mb-ring { stroke-dasharray: ${RING}; animation: mb-fill 1.6s cubic-bezier(.4,0,.2,1) infinite; }
.mb-ghost {
  background-image: linear-gradient(90deg, rgba(36,23,8,.05) 0%, rgba(36,23,8,.11) 45%, rgba(36,23,8,.05) 70%);
  background-size: 420px 100%;
  background-repeat: no-repeat;
  animation: mb-shim 1.3s linear infinite;
  border-radius: 7px;
}
@media (prefers-reduced-motion: reduce) {
  .mb-hand, .mb-ring, .mb-ghost { animation: none; }
  .mb-ring { stroke-dashoffset: ${Math.round(RING * 0.25)}; }
  .mb-ghost { background-image: linear-gradient(90deg, rgba(36,23,8,.08), rgba(36,23,8,.08)); }
}
`;

/** The stylesheet the dial needs, for anything that draws the dial on its own. */
export const CHRONO_CSS = CSS;

/**
 * The dial itself, on its own.
 *
 * Pulled out of the loader below so that the board's waiting state and the
 * overlay shown while an organizer is attaching or importing are the SAME
 * stopwatch, not two drawings of one. Whoever renders it must also render
 * CHRONO_CSS, which carries the sweep and the ring.
 */
export function ChronoDial({ size = 84 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" aria-hidden="true" style={{ flex: 'none' }}>
      {/* the dial */}
      <circle cx="60" cy="60" r="52" fill="none" stroke={HAIR} strokeWidth="9" />
      {/* the ring filling it — drawn from twelve o'clock */}
      <circle
        className="mb-ring"
        cx="60" cy="60" r="52" fill="none" stroke={ACCENT} strokeWidth="9"
        strokeLinecap="round" transform="rotate(-90 60 60)"
      />
      {/* the crown, so it reads as a stopwatch and not a progress circle */}
      <rect x="52" y="1" width="16" height="10" rx="3" fill={INK} />
      <g className="mb-hand">
        <rect x="57.5" y="24" width="5" height="38" rx="2.5" fill={INK} />
      </g>
      <circle cx="60" cy="60" r="6" fill={INK} />
    </svg>
  );
}

/** One ghost row, at the real table row's height and column widths. */
function GhostRow({ widths }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 14, height: 60,
        padding: '0 14px 0 26px', borderBottom: '1px solid rgba(36,23,8,.07)',
      }}
    >
      {widths.map((width, i) => (
        <span
          key={i}
          className="mb-ghost"
          style={{ height: 12, width, flex: i === 1 ? 1 : '0 0 auto' }}
        />
      ))}
    </div>
  );
}

const ROWS = [
  ['18px', '38%', '64px', '58px'],
  ['18px', '52%', '64px', '58px'],
  ['18px', '44%', '64px', '58px'],
  ['18px', '58%', '64px', '58px'],
  ['18px', '34%', '64px', '58px'],
];

export default function BoardLoader({
  label = 'Counting everybody in',
  // The second line. Defaulted to the board's own wording so every existing
  // caller is unchanged; the run detail page says what IT is waiting for.
  note = 'Reading every result for this race.',
}) {
  return (
    <div role="status" aria-live="polite" style={{ padding: '18px 0 6px' }}>
      <style>{CSS}</style>

      {/* Centred, with the words under the dial: the eye lands on the moving
          thing first and reads the explanation second. Side by side, the dial
          was just a bullet point next to a sentence. */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center', gap: 16, padding: '26px 0 30px',
      }}
      >
        <ChronoDial size={84} />

        <div>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 700, color: INK, letterSpacing: '-.012em' }}>
            {label}&hellip;
          </p>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: MUTED }}>
            {note}
          </p>
        </div>
      </div>

      {/* The card the real table lands in, at the same radius, with a ghost of
          its tab strip on top — so the page fills in rather than jumping. */}
      <div style={{ border: `1px solid ${HAIR}`, borderRadius: 22, overflow: 'hidden', background: '#fff' }}>
        <div
          aria-hidden="true"
          style={{
            display: 'flex', gap: 8, padding: '14px 16px', background: '#FAF7F4',
            borderBottom: `1px solid ${HAIR}`,
          }}
        >
          {[82, 96, 112, 70].map((width, i) => (
            <span key={i} className="mb-ghost" style={{ height: 14, width, borderRadius: 8 }} />
          ))}
        </div>
        {ROWS.map((widths, i) => <GhostRow key={i} widths={widths} />)}
      </div>

      {/* Said once, for a screen reader, without the decoration above. */}
      <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        Loading results
      </span>
    </div>
  );
}
